import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { trackEvent } from "../lib/tracking";
import { ArrowUpRight, CaretLeft, CaretRight, TrendUp } from "@phosphor-icons/react";

const ROTATION_MS = 5500;

export default function BestDealsHero() {
  const [items, setItems] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.get("/public/best-deals-this-week?limit=6")
      .then((r) => { if (alive) setItems(r.data?.items || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const advance = useCallback((dir = 1) => {
    setIndex((i) => {
      if (!items.length) return 0;
      return (i + dir + items.length) % items.length;
    });
  }, [items.length]);

  useEffect(() => {
    if (paused || items.length < 2) return;
    timerRef.current = setInterval(() => advance(1), ROTATION_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, items.length, advance]);

  if (!items.length) return null;
  const current = items[index];
  const priceLabel = current.cash_price ? `from $${Math.round(current.cash_price)}` : (current.points_price ? `from ${current.points_price.toLocaleString()} pts` : "Tap to book");

  const handleCtaClick = () => {
    trackEvent({
      event_type: "booking_click",
      origin: current.origin,
      destination: current.destination,
      airline: current.airline,
      cabin_class: current.cabin_class,
      price_cash: current.cash_price,
      price_points: current.points_price,
      article_id: `hero_carousel:${current.slug || current.id}`,
    });
  };

  return (
    <section
      className="container-page py-12 border-t border-[#D6CFC4]"
      data-testid="best-deals-hero"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline text-[#B4533C] flex items-center gap-2" data-testid="best-deals-overline">
            <TrendUp size={14} weight="regular" /> Best Deals · This Week
          </div>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight mt-2">What readers are booking right now</h2>
        </div>
        <Link to="/deals" className="overline text-[#1B3B36] hover:text-[#B4533C]" data-testid="best-deals-all-link">All deals →</Link>
      </div>

      <div className="grid md:grid-cols-12 gap-8 md:gap-10 items-stretch">
        {/* Image side */}
        <div className="md:col-span-7 relative">
          <div className="aspect-[5/4] md:aspect-[16/10] bg-[#EFECE5] overflow-hidden relative" data-testid="best-deals-image-frame">
            {items.map((d, i) => (
              <img
                key={d.id || d.slug || i}
                src={d.featured_image || ""}
                alt={d.title}
                loading={i === 0 ? "eager" : "lazy"}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[900ms] ease-out ${i === index ? "opacity-100" : "opacity-0"}`}
              />
            ))}
            {/* Trending badge */}
            {current.clicks_this_week > 0 && (
              <div className="absolute top-4 left-4 bg-[#1B3B36] text-[#F9F9F6] px-3 py-1 text-[10px] font-mono tracking-[0.15em] uppercase flex items-center gap-1" data-testid="best-deals-trending-badge">
                <TrendUp size={12} weight="regular" /> {current.clicks_this_week} clicks / 7d
              </div>
            )}
            {/* Controls */}
            {items.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => advance(-1)}
                  aria-label="Previous deal"
                  data-testid="best-deals-prev-btn"
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#F9F9F6]/85 backdrop-blur-sm text-[#1B3B36] hover:bg-[#1B3B36] hover:text-[#F9F9F6] transition-colors border border-[#D6CFC4]"
                >
                  <CaretLeft size={16} weight="regular" />
                </button>
                <button
                  type="button"
                  onClick={() => advance(1)}
                  aria-label="Next deal"
                  data-testid="best-deals-next-btn"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-[#F9F9F6]/85 backdrop-blur-sm text-[#1B3B36] hover:bg-[#1B3B36] hover:text-[#F9F9F6] transition-colors border border-[#D6CFC4]"
                >
                  <CaretRight size={16} weight="regular" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Copy side */}
        <div className="md:col-span-5 flex flex-col justify-between" data-testid={`best-deals-card-${current.slug || current.id}`}>
          <div>
            <div className="overline text-[#4A4742] flex items-center gap-3" data-testid="best-deals-route">
              <span>{current.origin || "—"}</span>
              <span className="text-[#B4533C]">→</span>
              <span>{current.destination || "—"}</span>
              {current.airline && <><span>·</span><span className="text-[#4A4742]">{current.airline}</span></>}
            </div>
            <h3 className="font-serif text-3xl md:text-4xl leading-[1.05] mt-3 text-[#1C1A17]" data-testid="best-deals-title">
              {current.title}
            </h3>
            {current.summary && (
              <p className="mt-4 text-base text-[#4A4742] leading-relaxed line-clamp-4" data-testid="best-deals-summary">{current.summary}</p>
            )}

            <div className="mt-6 grid grid-cols-2 grid-borders border-t border-l border-[#D6CFC4]">
              <div className="p-4">
                <div className="overline text-[#4A4742]">Cash</div>
                <div className="font-mono text-lg" data-testid="best-deals-price-cash">{current.cash_price ? `$${current.cash_price}` : "—"}</div>
              </div>
              <div className="p-4">
                <div className="overline text-[#4A4742]">Points</div>
                <div className="font-mono text-lg" data-testid="best-deals-price-points">{current.points_price ? current.points_price.toLocaleString() : "—"}</div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <Link
              to={`/deal/${current.slug}`}
              onClick={handleCtaClick}
              className="inline-flex items-center gap-2 bg-[#1B3B36] text-[#F9F9F6] px-6 py-3 text-sm tracking-wide hover:bg-[#B4533C] transition-colors"
              data-testid="best-deals-cta"
            >
              Read + book {priceLabel} <ArrowUpRight size={16} weight="regular" />
            </Link>

            {/* Dots */}
            {items.length > 1 && (
              <div className="flex items-center gap-2 ml-auto" data-testid="best-deals-dots">
                {items.map((d, i) => (
                  <button
                    key={d.id || d.slug || `dot-${i}`}
                    type="button"
                    onClick={() => setIndex(i)}
                    aria-label={`Show deal ${i + 1}`}
                    data-testid={`best-deals-dot-${i}`}
                    className={`h-[3px] transition-all duration-500 ${i === index ? "w-8 bg-[#1B3B36]" : "w-4 bg-[#D6CFC4] hover:bg-[#4A4742]"}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
